import { useState } from 'react';
import { templates as klicklyTemplates } from '@klickly/klickly-templates';
import { throttle } from 'lodash';
import * as find from 'lodash/find';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as map from 'lodash/map';
import * as uniqBy from 'lodash/uniqBy';
import { v4 as uuidv4 } from 'uuid';
import { analyticsAPI, giftly, widgetPromotionsAPI } from '../config/api';
import { klicklyBrandingClassNamesMap } from '../constants/klicklyBrandingClassNamesMap';
import {
    analyticsEventMap,
    INTERACT_ELEMENT_KEYS,
    liveRampEventMap,
    PROMOTIONS_DEFAULT_PARAMS,
    widgetSizeCountProductsInUnit
} from '../shared/constants';
import { LOCAL_TRANDING_LAYOUTS } from '../shared/constants/layout';
import { xhrGet } from '../shared/services/http';
import { getNeededUnit, getWidgetSizeCountProductsInUnit, isSingleViewLayout } from '../shared/utils';
import { TrackCustomEvent } from '../shared/utils/events';
import getParam from '../shared/utils/getParam';
import { create as createIframe } from '../shared/utils/iframe';
import { load as loadLiverampPixel } from '../shared/utils/liveramp';
import { addKlicklyUTMParams, appendBasisExcludeScript, loadURL, timeElapsed } from '../shared/utils/static';
import { BrowserStorage } from '../storage/Browser';
import { getData, getTypeOfUnit, setData } from './stores/data';
import { TouchData } from './stores/touch';

const getClickedElementRef = (className) => {
    if (className.indexOf('__track-logo') !== -1) return 'logo';
    if (className.indexOf('__track-offerMessage') !== -1) return 'offer-message';
    if (className.indexOf('view-carousel') !== -1) return 'blank-space';
    if (className.indexOf('slick-prev') !== -1) return 'arrows';
    if (className.indexOf('slick-next') !== -1) return 'arrows';

    return null;
};

const numberToMongodbId = (id) => {
    if (!id) return '';
    const sId = id.toString();
    return `${'0'.repeat(24 - id.length)}${sId}`;
};

export function useData() {
    const [isSpinner, setIsSpinner] = useState(true);
    const touchData = new TouchData();
    let interactElements = {};

    const getWidgetSize = () => {
        const size = `${getData('width')}x${getData('height')}`;
        if (Object.keys(widgetSizeCountProductsInUnit).includes(size)) return size;
        return null;
    };

    const handleUserEvent = (uniqId, isEnd = false, trackData = []) => {
        // Don't track user events (mouseover/touchmove) in study mode
        if (getData('isStudyMode')) {
            return;
        }

        // start mouseover/touchmove
        if (!isEnd) {
            if (interactElements[uniqId]) {
                interactElements[uniqId].resume();
            } else {
                interactElements[uniqId] = new TrackCustomEvent(trackData);
            }
        } else if (interactElements[uniqId]) {
            interactElements[uniqId].pause();
        }
    };

    const handleMouseMove = (uniqId, className) => {
        if (klicklyBrandingClassNamesMap.includes(className) && uniqId in interactElements) {
            interactElements[uniqId].addBrandedLogoElem('klicklyBrandingLogo');
        }
    };

    const handleMouseEnter = (promotion) => {
        // Don't send mouse enter events in study mode
        if (getData('isStudyMode')) {
            return;
        }

        if (window.parent) {
            window.parent.postMessage({ promotionMouseOver: true, promotion: promotion }, '*');
        }
    };

    const sendAllMouseOver = () => {
        // Don't track mouseover events in study mode
        if (getData('isStudyMode')) {
            interactElements = {};
            return;
        }

        // Prefer product events, widget-window only as fallback
        const elementsKeys = Object.keys(interactElements);
        const productKeys = elementsKeys.filter((k) => k !== INTERACT_ELEMENT_KEYS.WIDGET_WINDOW);
        const keysToSend = productKeys.length > 0 ? productKeys : elementsKeys;

        const pData = flatten(keysToSend.map((key) => interactElements[key].end()));
        interactElements = {};

        sendEvent({
            eventName: 'mouseover',
            promotions: pData,
            additionalData: { mouseoverLength: get(pData, '0.eventLength', 0) },
            event: get(pData, '0.eventElement', '')
        });
    };

    const throttledTouchMove = throttle((event) => {
        const touchedProductId = touchData.getTouchedProductId(event);

        if (!touchedProductId) {
            handleUserEvent(INTERACT_ELEMENT_KEYS.WIDGET_WINDOW, false, getData('uniqBrands'));
            return;
        }

        const touchedPromotion = getData('promotions').find((promotion) => promotion._id === touchedProductId);
        handleUserEvent(touchedProductId, false, [touchedPromotion]);
    }, 25);

    const handleTouchStart = (event) => {
        touchData.setFirstTouchEvent(event);
    };

    const sendTouchStart = () => {
        // Don't track touch events in study mode
        if (getData('isStudyMode')) {
            return;
        }

        const event = touchData.firstTouchEvent;
        const touchedProductId = touchData.getTouchedProductId(touchData.firstTouchEvent);
        const promotion = find(getData('promotions'), { _id: touchedProductId });

        const touch = event.touches[0];
        touchData.setStartData(event.timeStamp, touch);

        const data = touchData.buildTouchData(touch, event);

        if (!promotion) {
            return sendEvent({
                eventName: 'touchstart',
                promotions: getData('uniqBrands') || [],
                typeOfUnit: getClickedElementRef(event.target.className),
                additionalData: { isShopLink: getData('link') === 'shop' },
                event,
                touch: data
            });
        }

        sendEvent({
            eventName: 'touchstart',
            promotions: [promotion],
            event,
            touch: data
        });
    };

    const handleTouchEnd = (event) => {
        const touchedProductId = touchData.getTouchedProductId(event);

        Object.keys(interactElements).forEach((uniqId) => {
            handleUserEvent(uniqId, true);
        });

        sendTouchEnd(event, touchedProductId);
    };

    const sendTouchMove = () => {
        // Don't track touch events in study mode
        if (getData('isStudyMode')) {
            return;
        }

        const pData = flatten(map(interactElements, (elem) => elem.end()));
        interactElements = {};

        if (!pData) {
            return;
        }

        sendEvent({
            eventName: 'touchmove',
            promotions: pData,
            additionalData: { touchMoveLength: get(pData, '0.eventLength', 0) },
            event: get(pData, '0.eventElement', ''),
            touch: {
                timestamp: touchData.timestampVector,
                moveVector: touchData.moveVector
            }
        });
    };

    const sendTouchEnd = (event, id) => {
        // Don't track touch events in study mode
        if (getData('isStudyMode')) {
            touchData.reset();
            return;
        }

        const promotion = find(getData('promotions'), { _id: id });

        const touch = event.changedTouches[0];
        touchData.setEndData(event.timeStamp, touch);

        const data = touchData.buildTouchData(touch, event);

        sendTouchStart();
        sendTouchMove();

        if (!promotion) {
            return sendEvent({
                eventName: 'touchend',
                promotions: getData('uniqBrands') || [],
                typeOfUnit: getClickedElementRef(event.target.className),
                event,
                touch: data
            });
        }

        sendEvent({
            eventName: 'touchend',
            promotions: [promotion],
            event,
            touch: data
        });

        touchData.reset();
    };

    const onClickAnyWhereHandler = (linkToOpenOnClick, event = null) => {
        // Don't open links in study mode
        if (getData('isStudyMode')) {
            return false;
        }

        if (!linkToOpenOnClick) return false;

        if (event && klicklyBrandingClassNamesMap.includes(event.target.className)) {
            window.open('https://klickly.com/');
            sendAllMouseOver();
            return false;
        }

        if (getData('eventSource') !== 'chrome-extension') {
            window.open(linkToOpenOnClick);
            sendAllMouseOver();
        }
    };

    const loadClickExclude = () => {
        if (!getData('isClickExclude')) {
            loadURL('https://cdn.klickly.com/pixels/click-exclude.js');
            setData('isClickExclude', true);
        }
    };

    const handleClickAnyWhere = (event) => {
        // Don't process clicks in study mode
        if (getData('isStudyMode')) {
            return;
        }

        loadClickExclude();
        dspClickTracking();

        sendEvent({
            eventName: 'clicked',
            promotions: getData('uniqBrands') || [],
            typeOfUnit: getClickedElementRef(event.target.className),
            additionalData: { isShopLink: getData('link') === 'shop' },
            event
        });
    };

    const dspClickTracking = () => {
        const macroUrl = getData('cm');

        if (macroUrl) {
            fetch(macroUrl, {
                mode: 'no-cors',
                method: 'GET'
            });
        }
    };

    const handleClickProduct = ({ event, id, openShopHome = false, customDesignDetails = {} }) => {
        // Don't process product clicks in study mode
        if (getData('isStudyMode')) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        dspClickTracking();

        const twPixelUrl = getData('twPixelUrl');

        if (twPixelUrl && twPixelUrl.searchParams && twPixelUrl.searchParams.get('track_view_event_on') === 'click') {
            createTwIframe();
        }

        const promotion = find(getData('promotions'), { _id: id });
        loadClickExclude();

        if (!promotion) return;

        const windowOpen = window.open();
        const typeOfUnit = getTypeOfUnit();

        sendAllMouseOver();
        // wait until events saved
        sendEvent({
            eventName: 'clicked',
            promotions: [promotion],
            typeOfUnit,
            event
        });

        const isV1 = getParam('version', 'v1') === 'v1';

        let linkToOpen = addKlicklyUTMParams(
            `${giftly.baseURL}${giftly.endPoints.product(isV1 ? promotion._id : numberToMongodbId(promotion._id))}`
        );

        if (typeOfUnit === 'to-brand') {
            const cid = BrowserStorage.get('_klpixel_cid') || undefined;
            // Canonical `_kl_u` — also attached to the redirect so the commission
            // pipeline can match by either `_klpixel_cid` (legacy path) or
            // `kl_u` (canonical path). widget-ssr appends both too via the
            // mirrored logic in _widget-client.js. Without this, kl_u-based
            // commission attribution can't see clicks originating from V1.
            const klu = BrowserStorage.get('_kl_u') || undefined;

            const { handle = null, sku = null, connectedDomain, storeType = 'shopify' } = promotion;

            if (customDesignDetails?.redirectUrl) {
                linkToOpen = addKlicklyUTMParams(customDesignDetails?.redirectUrl);

                const url = new URL(linkToOpen);

                if (cid) {
                    url.searchParams.set('_klpixel_cid', cid);
                }
                if (klu) {
                    url.searchParams.set('kl_u', klu);
                }

                linkToOpen = url.toString();
            } else if (promotion.productUrl) {
                // Exists only on v2 with utm_params
                const extras = [cid ? `_klpixel_cid=${cid}` : null, klu ? `kl_u=${encodeURIComponent(klu)}` : null]
                    .filter(Boolean)
                    .join('&');
                linkToOpen = promotion.productUrl + (extras ? `&${extras}` : '');
            } else {
                linkToOpen = generateLinkToOpen({
                    openShopHome,
                    connectedDomain,
                    utmParams: promotion?.utmParams,
                    cid,
                    klu,
                    storeType,
                    handle,
                    sku
                });
            }
        }

        windowOpen.location.replace(linkToOpen);
    };

    const generateLinkToOpen = ({
        openShopHome = false,
        connectedDomain,
        utmParams,
        cid = null,
        klu = null,
        storeType,
        handle,
        sku = null
    }) => {
        const productPaths = {
            shopify: handle?.length ? `/products/${handle}` : '',
            magento: sku?.length ? `/${sku}.html` : ''
        };

        const urlSearchParams = new URLSearchParams(utmParams);

        if (cid) {
            urlSearchParams.set('_klpixel_cid', cid);
        }
        if (klu) {
            urlSearchParams.set('kl_u', klu);
        }

        return !openShopHome
            ? `https://${connectedDomain}${productPaths[storeType] || ''}?${urlSearchParams.toString()}`
            : `https://${connectedDomain}?${urlSearchParams.toString()}`;
    };

    const getPromotions = async () => {
        const initObject = { ...PROMOTIONS_DEFAULT_PARAMS };
        const pathname = widgetPromotionsAPI.endPoints.promotions.getPromotions[getData('version')];
        const baseUrl = widgetPromotionsAPI.baseURL[getData('version')];

        const url = new URL(`${baseUrl}${pathname}`);
        const unit = getNeededUnit(initObject.width, initObject.height);
        if (!unit) {
            return false;
        }

        const layout = getData('layout') || getParam('layout', '');
        const unitLayoutKey = `Unit${initObject.width}x${initObject.height}`;
        const unitLayouts = klicklyTemplates[unitLayoutKey]?.LAYOUTS;
        const countProductsInUnit =
            unitLayouts?.[layout]?.count ||
            unitLayouts?.default?.count ||
            getWidgetSizeCountProductsInUnit(initObject.width, initObject.height);

        const params = {
            page: 1,
            perPage: countProductsInUnit,
            adsSource: getParam('adsSource', 'widget'),
            width: initObject.width,
            height: initObject.height
        };

        if (initObject.p) {
            params.p = initObject.p;
        }

        if (initObject.brandId) {
            params.brandId = initObject.brandId;
        }

        if (initObject.campaignId) {
            params.campaignId = initObject.campaignId;
        }

        if (initObject.updateCache) {
            params.updateCache = initObject.updateCache;
        }

        if (initObject.allowAllSessions) {
            params.allowAllSessions = initObject.allowAllSessions;
        }

        if (layout) {
            params.layout = layout;
        }

        const fetchedData = await xhrGet(url.toString(), params);

        const {
            promotions = [],
            viewSettings = {},
            exclude = false,
            excludeStudy = false,
            pixels = [],
            aggregatedData = {},
            creativeDetails = null,
            customDesignDetails = null
        } = fetchedData || {};

        if (initObject.p && !initObject.brandId && promotions[0]) {
            initObject.brandId = promotions[0].account;
        }

        if (
            promotions.length &&
            promotions.length < countProductsInUnit &&
            !customDesignDetails?.redirectUrl &&
            !isSingleViewLayout(layout)
        ) {
            let i = 0;
            do {
                promotions.push({ ...promotions[i] });
                i++;
            } while (promotions.length < countProductsInUnit);
        }

        initObject.promotions = promotions;
        initObject.viewSettings = viewSettings;
        initObject.exclude = exclude;
        initObject.excludeStudy = excludeStudy;
        initObject.pixels = pixels;
        initObject.maxPriceDifferencePercent = aggregatedData?.maxPriceDifferencePercent || 0;
        initObject.creativeDetails = creativeDetails;
        initObject.customDesignDetails = customDesignDetails;

        return initObject;
    };

    const init = async (params) => {
        const { adId, eventSource, layout, cm, layoutType, version = 'v1', imageOptimizationUrl = null } = params;
        setData('adId', adId);
        setData('eventSource', eventSource);
        setData('layout', layout);
        setData('layoutType', layoutType);
        setData('cm', cm);
        setData('version', version);
        setData('imageOptimizationUrl', imageOptimizationUrl);

        const result = await getPromotions();

        if (!result) return;

        // Persist canonical `_kl_u` token returned by retargeting-api so
        // pixel.js's `readToken()` sees a value when attaching `u` to events.
        // Mirrors the cookie minted server-side; we write to all storages so
        // any later pixel event (incl. before widget mounts further) finds it.
        // Without this, V1 events shipped to analytics-api land without `u`.
        if (result.u && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result.u)) {
            try {
                BrowserStorage.set('_kl_u', result.u);
            } catch (_) {
                /* storage may be blocked */
            }
        }

        if (window.klpixel) {
            window.klpixel('meta', 'size', {
                width: result.width,
                height: result.height
            });

            const uniqueAccountIds = Array.from(new Set(result.promotions?.map((promotion) => promotion.account)));
            window.klpixel('config', 'accounts', uniqueAccountIds);
        }

        if (!layout && result.creativeDetails) {
            setData('layout', result.creativeDetails.layout);
            setData('layoutGroup', result.creativeDetails.group);
            if (window.klpixel) {
                window.klpixel('meta', 'creativeDetails', result.creativeDetails);
            }
        }

        const { brandId: account = null } = result;
        sendEvent({
            eventName: 'impression',
            promotions: [{ account, _id: null, campaign: null }]
        });

        const eventData = { adId, impression: true, accountId: account };

        if (window.klpixel) {
            window.klpixel('event', 'pageView', { widget: eventData });
        }

        if (!result.promotions && !result.promotions.length) return;

        result.adId = adId;

        setData('width', result.width);
        setData('height', result.height);

        setData('brandId', result.brandId);
        setData('link', result.link);
        setData(
            'uniqBrands',
            uniqBy(result.promotions, 'account').map(({ account, shopDomain }) => {
                return {
                    _id: null,
                    campaign: null,
                    account,
                    shopDomain
                };
            })
        );

        setData('promotions', result.promotions);
        setData('viewSettings', result.viewSettings);
        setData('pixels', result.pixels);
        setData('customDesignDetails', result.customDesignDetails);
        setData('maxPriceDifferencePercent', result.maxPriceDifferencePercent);

        // Check if promotions have studyId and studyGroup fields (study mode)
        const isStudyMode = result.promotions.some(
            (promotion) => promotion.studyId !== undefined && promotion.studyGroup !== undefined
        );
        setData('isStudyMode', isStudyMode);

        if (result.pixels.length) {
            result.pixels.forEach((pixel) => {
                if (pixel.name === 'tw') {
                    enableTWTracking(pixel);
                }
            });
        }

        // Evaluation layout shows one product at a time, so only the visible
        // (first) product is impressed at mount; reporting the whole batch would
        // over-count impressions (skewing CTR / AWA). Per-advance impressions are
        // phase 2. Other layouts report the full rendered set. KLIC-11308.
        sendEvent({
            eventName: 'loaded',
            promotions: isSingleViewLayout(layout) ? result.promotions.slice(0, 1) : result.promotions
        });

        if (result.exclude) loadURL('https://cdn.klickly.com/pixels/exclude.js');

        if (result.excludeStudy) appendBasisExcludeScript();

        // Keep spinner visible in study mode, otherwise hide it if promotions exist
        setIsSpinner(isStudyMode || !result.promotions.length);

        if (analyticsAPI.url && LOCAL_TRANDING_LAYOUTS.includes(layout)) {
            const fetchGeo = async () => {
                try {
                    const res = await fetch(`${analyticsAPI.url}/geo`, { credentials: 'omit' });
                    return await res.json();
                } catch {
                    return { city: 'near you' };
                }
            };

            setData('fetchGeo', fetchGeo);
        }
    };

    const createTwIframe = () => {
        return createIframe({ id: 'kl_tw_iframe', src: getData('twPixelUrl') });
    };

    const enableTWTracking = (pixel) => {
        const twPixelUrl = new URL(pixel.src);
        setData('twPixelUrl', twPixelUrl);

        if (twPixelUrl.searchParams.get('track_view_event_on') === 'view') {
            createTwIframe();
        }
    };

    const sendEvent = ({
        eventName,
        promotions,
        typeOfUnit = null,
        additionalData = {},
        event = null,
        touch = null
    }) => {
        const widgetSize = getWidgetSize();
        const isStudyMode = getData('isStudyMode');

        if (!getData('promotions').length || !widgetSize || !promotions.length) return false;

        // In study mode, only allow 'loaded' event (pageView), block all other events
        if (isStudyMode && eventName !== 'loaded') {
            return false;
        }

        const servedInfo = {
            eventId: uuidv4(),
            isLogIn: false,
            timeElapsed: timeElapsed(),
            adId: getData('adId'),
            widgetSize
        };

        // loop only once & prepare track, pixel, uniq accounts data
        const pData = promotions.reduce(
            (acc, promotion) => {
                if (analyticsEventMap[eventName]) {
                    const eventDataset = event?.target?.dataset;
                    const eventDatasetLength = eventDataset ? Object.keys(event.target.dataset).length : 0;
                    const price = get(promotion, 'variants.[0].price');
                    let linkToOpen = null;

                    if (!promotion?.productUrl) {
                        linkToOpen = generateLinkToOpen({
                            openShopHome: false,
                            ...promotion
                        });
                    }

                    acc.analyticEvents.push({
                        widget: {
                            adId: getData('adId'),
                            size: servedInfo.widgetSize,
                            element: event
                                ? {
                                      id: get(event, 'target.id') || undefined,
                                      className: get(event, 'target.class') || undefined,
                                      tagName: get(event, 'target.tagName', '').toLowerCase() || undefined,
                                      text: get(event, 'target.childNodes.0.nodeValue') || undefined,
                                      src: get(event, 'target.src') || undefined,
                                      href: get(event, 'target.href') || undefined,
                                      dataset: eventDatasetLength ? event.target.dataset : undefined
                                  }
                                : undefined,
                            promotion: {
                                _id: promotion._id || undefined,
                                externalId: promotion.externalId || undefined,
                                title: promotion.title || undefined,
                                accountId: promotion.account || undefined,
                                campaignId: promotion.campaign || undefined,
                                campaignName: promotion.campaignName || undefined,
                                brandName: promotion.brandName || undefined,
                                originalProductUrl: promotion?.productUrl || linkToOpen,
                                qty: 1,
                                price: Number(price),
                                shopDomain: promotion.shopDomain,
                                version: getData('version') || 'v1'
                            },
                            ...(additionalData || {})
                        }
                    });
                }

                if (liveRampEventMap[eventName]) {
                    acc.liverampEvents.push({
                        unit: getData('adId'),
                        action: liveRampEventMap[eventName],
                        promotion: promotion._id || undefined,
                        account: promotion.account || undefined,
                        campaign: promotion.campaign || undefined,
                        shopDomain: promotion.shopDomain || undefined,
                        typeOfUnit
                    });
                }

                return acc;
            },
            { analyticEvents: [], liverampEvents: [] }
        );

        if (analyticsEventMap[eventName] && window.klpixel) {
            window.klpixel(
                'event',
                analyticsEventMap[eventName],
                pData.analyticEvents,
                {
                    touchTime: touchData.timestampVector
                },
                { touch }
            );
        }

        if (liveRampEventMap[eventName] && !isStudyMode) {
            loadLiverampPixel({
                platform: 'klickly',
                source: getData('eventSource'),
                events: pData.liverampEvents
            });
        }

        return true;
    };

    // Phase 2 (KLIC-11308) — per-product impression + generic custom events for
    // experimental carousel layouts (e.g. Evaluation). These fire klpixel directly
    // so a custom `ev` name (`<layout>-like`) bypasses analyticsEventMap and the
    // click/liveramp path. Mirrors widget-ssr app/_widget-client.js.
    const findPromotionById = (id) =>
        getData('promotions').find((p) => String(p?._id) === String(id) || String(p?.id) === String(id)) || null;

    const buildWidgetPromotionRow = (promotion) => ({
        widget: {
            adId: getData('adId'),
            size: getWidgetSize(),
            promotion: {
                _id: promotion?._id || undefined,
                externalId: promotion?.externalId || undefined,
                accountId: promotion?.account || undefined,
                campaignId: promotion?.campaign || undefined,
                shopDomain: promotion?.shopDomain || undefined
            }
        }
    });

    // One pageView per product as it comes into view. Internal pageView only —
    // LiveRamp/TTD stay at the mount impression. Suppressed in study mode.
    const onProductView = (id) => {
        if (getData('isStudyMode') || !window.klpixel) return;
        const promotion = findPromotionById(id);
        if (!promotion) return;
        window.klpixel('event', 'pageView', [buildWidgetPromotionRow(promotion)]);
    };

    // Generic custom-event emitter. `name` is fired verbatim as the klpixel `ev`;
    // optional `productId` attaches the product. Suppressed in study mode.
    const trackCustomEvent = ({ name, productId } = {}) => {
        if (!name || getData('isStudyMode') || !window.klpixel) return;
        const promotion = productId != null ? findPromotionById(productId) : null;
        const row = promotion
            ? buildWidgetPromotionRow(promotion)
            : { widget: { adId: getData('adId'), size: getWidgetSize() } };
        window.klpixel('event', name, [row]);
    };

    return {
        isSpinner,
        init,
        handleTouchStart,
        throttledTouchMove,
        handleTouchEnd,
        handleUserEvent,
        handleMouseEnter,
        handleMouseMove,
        sendAllMouseOver,
        onClickAnyWhereHandler,
        handleClickAnyWhere,
        handleClickProduct,
        sendEvent,
        onProductView,
        trackCustomEvent
    };
}
